// DashboardEnvioPage.js — Dashboard en tiempo real del envío de un contenido (Axon).
// Se auto-refresca; misma estructura del informe consolidado (tarjetas enviado/
// recibido/visto/evaluaciones + desglose por audiencia + embudo).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

const BASE = `${API_BASE_URL}/wa-progreso.php`;
const LLAVE = 'dash_envio_at';
const leerToken = () => { try { return sessionStorage.getItem(LLAVE) || ''; } catch { return ''; } };
const guardarToken = (t) => { try { if (t) sessionStorage.setItem(LLAVE, t); else sessionStorage.removeItem(LLAVE); } catch {} };
// Cliente propio (sin el interceptor global, que pondría el token de la sesión de la plataforma):
// cada llamada lleva el token de este dashboard; un 401 devuelve a la pantalla del correo.
const cliente = axios.create();
cliente.interceptors.request.use(c => { const t = leerToken(); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });
cliente.interceptors.response.use(r => r, e => {
  if (e?.response?.status === 401) { guardarToken(''); window.dispatchEvent(new Event('dash-envio-salir')); }
  return Promise.reject(e);
});
const REFRESH_MS = 15000;

const Page = styled.div`
  min-height: 100vh; background: #eef1f6; padding: 1.2rem;
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; color: #0f172a;
`;
const Shell = styled.div`max-width: 1100px; margin: 0 auto;`;
const Header = styled.div`
  background: linear-gradient(120deg, #1e3a8a, #1d4ed8); color: #fff;
  border-radius: 14px; padding: 1.1rem 1.4rem; display: flex; justify-content: space-between;
  align-items: center; flex-wrap: wrap; gap: 0.8rem; margin-bottom: 1rem;
`;
const Brand = styled.div`font-size: 0.8rem; letter-spacing: 1px; opacity: 0.85;`;
const Titulo = styled.h1`margin: 0.15rem 0 0; font-size: 1.25rem;`;
const Corte = styled.div`font-size: 0.8rem; opacity: 0.85; margin-top: 0.2rem;`;
const Live = styled.div`
  display: flex; align-items: center; gap: 0.4rem; font-size: 0.8rem; font-weight: 600;
  background: rgba(255,255,255,0.15); padding: 0.4rem 0.75rem; border-radius: 999px;
`;
const pulse = keyframes`0%,100%{opacity:1}50%{opacity:0.3}`;
const Dot = styled.span`width: 9px; height: 9px; border-radius: 50%; background: #22c55e; animation: ${pulse} 1.5s infinite;`;

const Toolbar = styled.div`display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap; margin-bottom: 1rem;`;
const Select = styled.select`
  padding: 0.55rem 0.8rem; border-radius: 9px; border: 1px solid #cbd5e1; font-size: 0.9rem; background: #fff;
`;

const Cards = styled.div`
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.8rem; margin-bottom: 1.1rem;
  @media (max-width: 700px) { grid-template-columns: repeat(2, 1fr); }
`;
const Card = styled.div`
  background: ${p => p.$bg}; color: #fff; border-radius: 12px; padding: 1.1rem 1rem; text-align: center;
`;
const Num = styled.div`font-size: 2.1rem; font-weight: 800; line-height: 1;`;
const Lbl = styled.div`font-size: 0.82rem; opacity: 0.92; margin-top: 0.3rem;`;
const Pct = styled.div`font-size: 1.05rem; font-weight: 700; margin-top: 0.35rem;`;

const Panel = styled.div`background: #fff; border-radius: 12px; padding: 1rem 1.1rem; margin-bottom: 1rem; box-shadow: 0 2px 10px rgba(15,23,42,0.05);`;
const PanelT = styled.div`font-weight: 700; color: #1e3a8a; font-size: 0.95rem; margin-bottom: 0.7rem;`;
const Table = styled.table`
  width: 100%; border-collapse: collapse; font-size: 0.86rem;
  th, td { padding: 0.5rem 0.6rem; text-align: right; }
  th:first-child, td:first-child { text-align: left; }
  thead th { background: #1e3a8a; color: #fff; font-weight: 600; }
  tbody tr:nth-child(even) { background: #f8fafc; }
  tfoot td { font-weight: 700; border-top: 2px solid #e2e8f0; }
`;
const Funnel = styled.div`display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap;`;
const FStep = styled.div`
  background: #1e3a8a; color: #fff; border-radius: 10px; padding: 0.7rem 0.9rem; text-align: center; flex: 1; min-width: 90px;
  .n { font-size: 1.5rem; font-weight: 800; } .l { font-size: 0.72rem; opacity: 0.9; }
`;
const Arrow = styled.div`color: #94a3b8; font-weight: 700;`;
const Muted = styled.div`color: #64748b; font-size: 0.8rem; margin-top: 0.5rem;`;

// --- Fallidos: lista bajo demanda ---
const Toggle = styled.button`
  background: #fff; border: 1px solid #cbd5e1; border-radius: 9px; padding: 0.55rem 0.9rem;
  font-size: 0.86rem; font-weight: 600; color: #1e3a8a; cursor: pointer; width: 100%;
  text-align: left; display: flex; justify-content: space-between; align-items: center;
  &:hover { background: #f8fafc; }
`;
const Motivos = styled.div`display: grid; gap: 0.6rem; margin: 0.9rem 0;`;
const Motivo = styled.div`
  background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 0.7rem 0.85rem;
  .h { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; }
  .t { font-weight: 700; color: #0f172a; font-size: 0.9rem; }
  .n { background: #ea580c; color: #fff; border-radius: 999px; padding: 0.1rem 0.6rem; font-size: 0.82rem; font-weight: 700; white-space: nowrap; }
  .d { color: #475569; font-size: 0.82rem; margin-top: 0.35rem; line-height: 1.35; }
`;
const Chip = styled.span`
  display: inline-block; padding: 0.1rem 0.5rem; border-radius: 999px; font-size: 0.75rem; font-weight: 600;
  background: ${p => p.$v === 'Video' ? '#fee2e2' : p.$v === 'Examen' ? '#e0e7ff' : '#fef3c7'};
  color: ${p => p.$v === 'Video' ? '#b91c1c' : p.$v === 'Examen' ? '#3730a3' : '#92400e'};
`;

// --- Pestañas + Aceptación T&C ---
const TabBar = styled.div`display: flex; gap: 0.4rem; margin-bottom: 1rem; flex-wrap: wrap;`;
const TabBtn = styled.button`
  padding: 0.55rem 1.05rem; border-radius: 9px; border: 1px solid #cbd5e1; cursor: pointer;
  font-size: 0.9rem; font-weight: 600;
  background: ${p => p.$on ? '#1e3a8a' : '#fff'}; color: ${p => p.$on ? '#fff' : '#1e3a8a'};
`;
const ExportBtn = styled.button`
  padding: 0.55rem 1rem; border-radius: 9px; border: none; cursor: pointer;
  font-size: 0.88rem; font-weight: 700; background: #15803d; color: #fff;
  display: inline-flex; align-items: center; gap: 0.4rem;
  &:hover { background: #166534; } &:disabled { opacity: 0.5; cursor: default; }
`;
const Input = styled.input`
  width: 100%; padding: 0.6rem 0.85rem; border-radius: 9px; border: 1px solid #cbd5e1; font-size: 0.9rem;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #1d4ed8; }
`;
const Est = styled.span`
  display: inline-block; padding: 0.12rem 0.6rem; border-radius: 999px; font-size: 0.76rem; font-weight: 700;
  background: ${p => p.$c === 'acepto' ? '#dcfce7' : p.$c === 'rechazo' ? '#fee2e2' : '#f1f5f9'};
  color: ${p => p.$c === 'acepto' ? '#166534' : p.$c === 'rechazo' ? '#b91c1c' : '#475569'};
`;
const MiniCards = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.7rem; margin-bottom: 1rem;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const MiniCard = styled.div`
  background: ${p => p.$bg}; color: #fff; border-radius: 12px; padding: 0.9rem 1rem; text-align: center;
  .n { font-size: 1.8rem; font-weight: 800; line-height: 1; } .l { font-size: 0.8rem; opacity: 0.92; margin-top: 0.25rem; }
`;

function PanelEnvio() {
  const [params, setParams] = useSearchParams();
  const progUrl = params.get('prog') || '';
  const [programas, setProgramas] = useState([]);
  const [prog, setProg] = useState(progUrl);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [fall, setFall] = useState(null);      // detalle de fallidos (bajo demanda)
  const [fallOpen, setFallOpen] = useState(false);
  const [fallLoad, setFallLoad] = useState(false);
  const [vista, setVista] = useState('contenidos'); // 'contenidos' | 'tyc'
  const [tyc, setTyc] = useState(null);
  const [busq, setBusq] = useState('');
  const [busqRes, setBusqRes] = useState(null);
  const [busqLoad, setBusqLoad] = useState(false);
  const timer = useRef(null);

  // Exporta CSV de lo que se está viendo (Contenidos o Aceptación T&C), con teléfono completo.
  // Descarga en el navegador.
  const exportar = useCallback(async () => {
    const enTyc = vista === 'tyc';
    if (!enTyc && !prog) { alert('Elige un contenido primero.'); return; }
    const url = `${BASE}?action=export&tipo=${enTyc ? 'tyc' : 'contenido'}${enTyc ? '' : `&programa=${prog}`}`;
    try {
      const res = await cliente.get(url, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : 'export.csv';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    } catch (e) {
      if (e?.response?.status !== 401) alert('No se pudo exportar. Intenta de nuevo.');
    }
  }, [vista, prog]);

  // Cargar resumen de aceptación T&C al entrar a la pestaña
  useEffect(() => {
    if (vista !== 'tyc') return;
    cliente.get(`${BASE}?action=tyc`).then(({ data: d }) => { if (d.ok) setTyc(d); }).catch(() => {});
  }, [vista]);

  // Buscador (debounced) de aceptación T&C
  useEffect(() => {
    if (vista !== 'tyc') return;
    const q = busq.trim();
    if (q.length < 3) { setBusqRes(null); return; }
    setBusqLoad(true);
    const id = setTimeout(() => {
      cliente.get(`${BASE}?action=tyc-buscar&q=${encodeURIComponent(q)}`)
        .then(({ data: d }) => { if (d.ok) setBusqRes(d); })
        .catch(() => {})
        .finally(() => setBusqLoad(false));
    }, 350);
    return () => clearTimeout(id);
  }, [busq, vista]);

  const verFallidos = useCallback(() => {
    const next = !fallOpen; setFallOpen(next);
    if (next && prog) {
      setFallLoad(true);
      cliente.get(`${BASE}?action=fallidos&programa=${prog}`)
        .then(({ data: d }) => { if (d.ok) setFall(d); })
        .catch(() => {})
        .finally(() => setFallLoad(false));
    }
  }, [fallOpen, prog]);

  useEffect(() => {
    cliente.get(`${BASE}?action=programas`).then(({ data: d }) => {
      if (d.ok) { setProgramas(d.programas); if (!prog && d.programas[0]) setProg(String(d.programas[0].id)); }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargar = useCallback((pid) => {
    if (!pid) return;
    cliente.get(`${BASE}?action=progreso&programa=${pid}`)
      .then(({ data: d }) => { if (d.ok) { setData(d); setErr(''); } else setErr('No se pudo cargar'); })
      .catch(() => setErr('Error de conexión'));
  }, []);

  useEffect(() => {
    if (!prog) return;
    setFall(null); setFallOpen(false);   // reinicia detalle al cambiar de contenido
    setParams(prev => { const p = new URLSearchParams(prev); p.set('prog', prog); return p; }, { replace: true });
    cargar(prog);
    clearInterval(timer.current);
    timer.current = setInterval(() => cargar(prog), REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [prog, cargar, setParams]);

  const t = data?.total;
  return (
    <Page>
      <Shell>
        <Header>
          <div>
            <Brand>ATENEO.DIGITAL · AXON PHARMA</Brand>
            <Titulo>{data ? data.programa.nombre : 'Dashboard de envío'}</Titulo>
            <Corte>{data ? `Corte en vivo · ${data.corte} · Bogotá` : 'Cargando…'}</Corte>
          </div>
          <Live><Dot /> EN VIVO</Live>
        </Header>

        <TabBar>
          <TabBtn $on={vista === 'contenidos'} onClick={() => setVista('contenidos')}>Contenidos</TabBtn>
          <TabBtn $on={vista === 'tyc'} onClick={() => setVista('tyc')}>Aceptación T&amp;C</TabBtn>
        </TabBar>

        {vista === 'contenidos' && (<>
        {programas.length > 0 && (
          <Toolbar>
            <Select value={prog} onChange={e => setProg(e.target.value)}>
              <option value="">— Elegir contenido —</option>
              {programas.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </Select>
            {err && <span style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{err}</span>}
            <ExportBtn onClick={exportar} disabled={!prog} style={{ marginLeft: 'auto' }}>⬇ Exportar CSV</ExportBtn>
          </Toolbar>
        )}

        {programas.length === 0 && (
          <Panel style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
            <div style={{ fontSize: '2.2rem', marginBottom: '0.5rem' }}>📡</div>
            <PanelT style={{ marginBottom: '0.3rem' }}>Aún no hay envíos en vivo</PanelT>
            <Muted>El progreso del próximo contenido aparecerá aquí en tiempo real apenas se dispare el envío.</Muted>
          </Panel>
        )}

        {t && t.enviado === 0 && (
          <Panel style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
            <div style={{ fontSize: '2.2rem', marginBottom: '0.5rem' }}>⏳</div>
            <PanelT style={{ marginBottom: '0.3rem' }}>El envío aún no ha comenzado</PanelT>
            <Muted>Este contenido está en fase de prueba interna. Los datos aparecerán aquí en tiempo real apenas se lance el envío a la audiencia.</Muted>
          </Panel>
        )}

        {t && t.enviado > 0 && (
          <>
            <Cards>
              <Card $bg="#c2410c"><Num>{t.enviado}</Num><Lbl>Enviado</Lbl><Pct>100%</Pct></Card>
              <Card $bg="#1d4ed8"><Num>{t.recibido}</Num><Lbl>Recibido</Lbl><Pct>{t.recibido_pct}%</Pct></Card>
              <Card $bg="#15803d"><Num>{t.visto}</Num><Lbl>Visto</Lbl><Pct>{t.visto_pct}%</Pct></Card>
              <Card $bg="#374151"><Num>{t.eval}</Num><Lbl>Evaluaciones</Lbl><Pct>{t.eval_pct}%</Pct></Card>
            </Cards>

            {data.avanzar && data.avanzar.enviados > 0 && (
              <Panel>
                <PanelT>Reenganche — plantilla "Avanzar" (médicos topados)</PanelT>
                <Funnel>
                  <FStep><div className="n">{data.avanzar.enviados}</div><div className="l">Aviso enviado</div></FStep>
                  <Arrow>›</Arrow>
                  <FStep><div className="n">{data.avanzar.tocaron}</div><div className="l">Tocaron "Avanzar" ({data.avanzar.tocaron_pct}%)</div></FStep>
                </Funnel>
                <Muted>
                  A estos médicos (topados por marketing) se les envió una plantilla de <b>utilidad</b> en lugar del video —
                  por eso <b>no cuentan como “Recibido” del video hasta que tocan “Avanzar”</b>. El “Recibido” de arriba sube a
                  medida que responden. Aparte del video de arriba.
                </Muted>
              </Panel>
            )}

            <Panel>
              <PanelT>Embudo de consumo</PanelT>
              <Funnel>
                <FStep><div className="n">{t.enviado}</div><div className="l">Enviado</div></FStep>
                <Arrow>›</Arrow>
                <FStep><div className="n">{t.recibido}</div><div className="l">Recibieron</div></FStep>
                <Arrow>›</Arrow>
                <FStep><div className="n">{t.visto}</div><div className="l">Visualizaron</div></FStep>
                {data.tiene_examen && <><Arrow>›</Arrow>
                  <FStep><div className="n">{t.eval}</div><div className="l">Presentaron</div></FStep>
                  <Arrow>›</Arrow>
                  <FStep><div className="n">{t.correctas}</div><div className="l">Aprobaron</div></FStep></>}
              </Funnel>
              <Muted>
                Fallidos (Meta no entregó): {t.fallido} · Enviados sin confirmar: {t.pendiente}
                {data.tiene_examen ? ` · Acierto: ${t.correctas_pct}%` : ''}
              </Muted>
            </Panel>

            <Panel>
              <PanelT>Desglose por audiencia</PanelT>
              <div style={{ overflowX: 'auto' }}>
                <Table>
                  <thead>
                    <tr><th>Audiencia</th><th>Enviado</th><th>Recibido</th><th>Visto</th>
                      {data.tiene_examen && <><th>Evaluaciones</th><th>Correctas</th></>}</tr>
                  </thead>
                  <tbody>
                    {data.audiencias.map(a => (
                      <tr key={a.audiencia}>
                        <td>{a.audiencia}</td>
                        <td>{a.enviado}</td>
                        <td>{a.recibido} ({a.recibido_pct}%)</td>
                        <td>{a.visto} ({a.visto_pct}%)</td>
                        {data.tiene_examen && <><td>{a.eval} ({a.eval_pct}%)</td><td>{a.correctas} ({a.correctas_pct}%)</td></>}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td>Total</td><td>{t.enviado}</td><td>{t.recibido} ({t.recibido_pct}%)</td><td>{t.visto} ({t.visto_pct}%)</td>
                      {data.tiene_examen && <><td>{t.eval} ({t.eval_pct}%)</td><td>{t.correctas} ({t.correctas_pct}%)</td></>}</tr>
                  </tfoot>
                </Table>
              </div>
            </Panel>

            {t.fallido > 0 && (
              <Panel>
                <Toggle onClick={verFallidos}>
                  <span>Ver lista de no entregados y su motivo ({t.fallido})</span>
                  <span>{fallOpen ? '▲' : '▼'}</span>
                </Toggle>
                {fallOpen && (
                  <>
                    {fallLoad && <Muted style={{ marginTop: '0.8rem' }}>Cargando detalle…</Muted>}
                    {fall && (
                      <>
                        <Motivos>
                          {fall.resumen.map(m => (
                            <Motivo key={m.titulo}>
                              <div className="h"><span className="t">{m.titulo}</span><span className="n">{m.n}</span></div>
                              <div className="d">{m.texto}</div>
                            </Motivo>
                          ))}
                        </Motivos>
                        <div style={{ overflowX: 'auto' }}>
                          <Table>
                            <thead>
                              <tr><th>Audiencia</th><th style={{ textAlign: 'left' }}>Médico</th><th style={{ textAlign: 'left' }}>Teléfono</th><th style={{ textAlign: 'left' }}>Qué falló</th><th style={{ textAlign: 'left' }}>Motivo</th></tr>
                            </thead>
                            <tbody>
                              {fall.fallidos.map((f, i) => (
                                <tr key={i}>
                                  <td>{f.audiencia}</td>
                                  <td style={{ textAlign: 'left' }}>{f.nombre}</td>
                                  <td style={{ textAlign: 'left', fontVariantNumeric: 'tabular-nums' }}>{f.tel}</td>
                                  <td style={{ textAlign: 'left' }}><Chip $v={f.que_fallo}>{f.que_fallo}</Chip></td>
                                  <td style={{ textAlign: 'left' }}>{f.motivo}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                        <Muted>Teléfono enmascarado por privacidad (tablero público). La lista completa con números va en el informe interno.</Muted>
                      </>
                    )}
                  </>
                )}
              </Panel>
            )}

            <Muted style={{ textAlign: 'center' }}>Se actualiza automáticamente cada {REFRESH_MS / 1000} segundos · cifras acumulativas</Muted>
          </>
        )}
        </>)}

        {vista === 'tyc' && (
          <>
            <Toolbar style={{ justifyContent: 'flex-end' }}>
              <ExportBtn onClick={exportar}>⬇ Exportar CSV (todas las audiencias)</ExportBtn>
            </Toolbar>
            <Panel>
              <PanelT>Buscar médico</PanelT>
              <Input
                placeholder="Nombre o teléfono (mín. 3 caracteres)…"
                value={busq}
                onChange={e => setBusq(e.target.value)}
              />
              {busqLoad && <Muted>Buscando…</Muted>}
              {busqRes && busqRes.resultados.length === 0 && busq.trim().length >= 3 && !busqLoad && (
                <Muted>Sin coincidencias para “{busq.trim()}”.</Muted>
              )}
              {busqRes && busqRes.resultados.length > 0 && (
                <div style={{ overflowX: 'auto', marginTop: '0.7rem' }}>
                  <Table>
                    <thead>
                      <tr><th style={{ textAlign: 'left' }}>Médico</th><th style={{ textAlign: 'left' }}>Audiencia</th><th style={{ textAlign: 'left' }}>Teléfono</th><th style={{ textAlign: 'left' }}>T&amp;C</th></tr>
                    </thead>
                    <tbody>
                      {busqRes.resultados.map((r, i) => (
                        <tr key={i}>
                          <td style={{ textAlign: 'left' }}>{r.nombre}</td>
                          <td style={{ textAlign: 'left' }}>{r.audiencia}</td>
                          <td style={{ textAlign: 'left', fontVariantNumeric: 'tabular-nums' }}>{r.tel}</td>
                          <td style={{ textAlign: 'left' }}><Est $c={r.cod}>{r.estado}</Est></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Muted>{busqRes.total >= 40 ? 'Mostrando los primeros 40 · afina la búsqueda' : `${busqRes.total} resultado(s)`} · teléfono enmascarado</Muted>
                </div>
              )}
            </Panel>

            {tyc && (
              <>
                <MiniCards>
                  <MiniCard $bg="#15803d"><div className="n">{tyc.total.aceptaron}</div><div className="l">Aceptaron ({tyc.total.aceptaron_pct}%)</div></MiniCard>
                  <MiniCard $bg="#b91c1c"><div className="n">{tyc.total.rechazaron}</div><div className="l">Rechazaron ({tyc.total.rechazaron_pct}%)</div></MiniCard>
                  <MiniCard $bg="#64748b"><div className="n">{tyc.total.sin}</div><div className="l">Sin responder ({tyc.total.sin_pct}%)</div></MiniCard>
                </MiniCards>

                <Panel>
                  <PanelT>Aceptación de T&amp;C por audiencia</PanelT>
                  <div style={{ overflowX: 'auto' }}>
                    <Table>
                      <thead>
                        <tr><th>Audiencia</th><th>Aceptaron</th><th>Rechazaron</th><th>Sin responder</th><th>Base</th></tr>
                      </thead>
                      <tbody>
                        {tyc.audiencias.map(a => (
                          <tr key={a.audiencia}>
                            <td>{a.audiencia}</td>
                            <td style={{ color: '#15803d', fontWeight: 700 }}>{a.aceptaron} ({a.aceptaron_pct}%)</td>
                            <td style={{ color: '#b91c1c' }}>{a.rechazaron} ({a.rechazaron_pct}%)</td>
                            <td style={{ color: '#64748b' }}>{a.sin} ({a.sin_pct}%)</td>
                            <td>{a.total}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Total</td>
                          <td>{tyc.total.aceptaron} ({tyc.total.aceptaron_pct}%)</td>
                          <td>{tyc.total.rechazaron} ({tyc.total.rechazaron_pct}%)</td>
                          <td>{tyc.total.sin} ({tyc.total.sin_pct}%)</td>
                          <td>{tyc.total.total}</td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                  <Muted>Aceptaron = dieron consentimiento · Rechazaron = respondieron que no · Sin responder = T&amp;C enviado, aún sin respuesta · corte {tyc.corte} Bogotá</Muted>
                </Panel>
              </>
            )}
          </>
        )}
      </Shell>
    </Page>
  );
}

// Entrada: correo autorizado → código de 6 dígitos al correo → token (12h, en esta pestaña).
export default function DashboardEnvioPage() {
  const [token, setToken] = useState(leerToken);
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [paso, setPaso] = useState('correo');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    const salir = () => { setToken(''); setPaso('correo'); setCodigo(''); setError('Tu acceso venció. Vuelve a ingresar tu correo.'); };
    window.addEventListener('dash-envio-salir', salir);
    return () => window.removeEventListener('dash-envio-salir', salir);
  }, []);

  if (token) return <PanelEnvio />;

  const enviar = (e) => {
    e.preventDefault(); setError(''); setCargando(true);
    const cuerpo = paso === 'correo' ? { email: email.trim() } : { email: email.trim(), codigo };
    axios.post(`${API_BASE_URL}/dashboard-envio-acceso.php`, cuerpo)
      .then(({ data: d }) => {
        if (paso === 'correo') { setPaso('codigo'); setMsg(d.message || ''); }
        else if (d.access_token) { guardarToken(d.access_token); setToken(d.access_token); }
      })
      .catch(err => setError(err.response?.data?.message || 'No se pudo continuar. Intenta de nuevo.'))
      .finally(() => setCargando(false));
  };

  return (
    <Page>
      <Shell style={{ maxWidth: 420, paddingTop: '12vh' }}>
        <Panel>
          <PanelT>Dashboard de envíos — WhatsApp Axon</PanelT>
          <form onSubmit={enviar} style={{ display: 'grid', gap: '0.7rem' }}>
            {paso === 'correo' ? (
              <Input type="email" placeholder="Tu correo autorizado" value={email} onChange={e => setEmail(e.target.value)} autoFocus required />
            ) : (
              <>
                {msg && <Muted>{msg}</Muted>}
                <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="Código de 6 dígitos" autoFocus
                       value={codigo} onChange={e => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                       style={{ letterSpacing: 6, textAlign: 'center', fontSize: '1.2rem' }} />
              </>
            )}
            <ExportBtn type="submit" disabled={cargando || (paso === 'codigo' && codigo.length !== 6)} style={{ justifyContent: 'center', background: '#1d4ed8' }}>
              {cargando ? 'Un momento…' : paso === 'correo' ? 'Enviarme un código' : 'Entrar'}
            </ExportBtn>
            {paso === 'codigo' && (
              <Toggle type="button" onClick={() => { setPaso('correo'); setCodigo(''); setError(''); }}>Usar otro correo o pedir un código nuevo</Toggle>
            )}
            {error && <Est $c="rechazo" style={{ textAlign: 'center' }}>{error}</Est>}
          </form>
        </Panel>
      </Shell>
    </Page>
  );
}
